import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Button from '../../../util_components/button';
import CustomCheckBox from '../../../util_components/custom_checkbox';
import CustomInput from '../../../util_components/custom_input';
import CustomRadioButton from '../../../util_components/custom_radio_button';
import CustomSingleSelect from '../../../util_components/custom_single_select';
import TeacherCancelBucketPicker from '../../../util_components/teacher_cancel_bucket_picker';
import { CareRequestFields, CareSaveRetry } from '../../../util_components/care_request';
import {
  IST,
  KIND_CANCEL,
  build_create_payload,
  care_api_error
} from '../../../util_components/care_request/care_request_rules';
import { post_api } from '../../../../redux/api_funcs';
import { get_api_url } from '../../../../utils/urls';
import { session_status_values } from '../../../../constants';
import EditReasonField from './edit_reason_field';
import useCareCapture from './use_care_capture';
import useCareCancelRecord from './use_care_cancel_record';
import {
  CARE_OWNS_CANCEL_TIME_NOTE,
  care_owns_cancel_time,
  edit_field_patch,
  edit_form_from,
  edit_reason_of,
  ignore_reason_patch,
  is_cancel_capture,
  plan_edit_save,
  toggle_cancel_patch,
  update_session_error_text,
  update_session_payload
} from './session_change_rules';

const cancel_options = ['cancelled_by_student', 'cancelled_by_teacher'];
const error_style = { color: 'var(--state-error)' };

// The session page's "Edit Session Details" body; mounted per open, so it starts from the loaded session.
// Ignore-rating and the page loading flag stay on the page, which seeds them from each load.
function EditSessionForm(props) {
  const { session_uuid, session_details, loading, ignore_rating, ignore_rating_reason } = props;
  const [form, setForm] = useState(() => edit_form_from(session_details));
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const care = useCareCapture({
    session_uuid,
    kind: KIND_CANCEL,
    on_saved: props.on_care_saved,
    on_retry_change: props.on_retry_change
  });
  const care_record = useCareCancelRecord(session_uuid, session_details);

  const on_field = (e) => set(edit_field_patch(e.target.name, e.target.value));
  const on_ignore_reason = (e) => {
    props.on_page_field_change(e);
    set(ignore_reason_patch());
  };
  const patch_care = (patch) => {
    care.patch(patch);
    if ('requested_at' in patch) set({ cancel_bucket_error: false });
  };

  const save = async () => {
    const plan = plan_edit_save({
      form,
      session_details,
      care_draft: care.draft,
      ignore_rating,
      ignore_rating_reason,
      now_ms: Date.now(),
      care_record
    });
    if (plan.errors) {
      set(plan.errors);
      if (plan.care_errors) care.setErrors(plan.care_errors);
      return;
    }
    props.set_loading(true);
    set({ save_modal_error: '' });
    let screenshot_key = '';
    try {
      screenshot_key = plan.capture ? await care.upload_once() : '';
    } catch (e) {
      props.set_loading(false);
      props.notify(
        'error',
        care_api_error(e, 'The screenshot could not be uploaded. Nothing was changed.').message
      );
      return;
    }
    const payload = update_session_payload({
      session_uuid,
      student_uuid: session_details.student_uuid,
      form,
      plan,
      ignore_rating,
      ignore_rating_reason
    });
    post_api(get_api_url('update_session'), payload, true)
      .then(() => {
        if (!plan.capture) {
          props.on_saved();
          return;
        }
        care.save_after_change(
          build_create_payload(care.draft, {
            session_uuid,
            note: plan.edit.reason,
            initiated_by: plan.initiator,
            screenshot_key
          }),
          props.on_saved
        );
      })
      .catch((e) => {
        // the modal stays open with the server's reason inline (e.g. the slot is taken)
        props.on_save_failed();
        set({ save_modal_error: update_session_error_text(e, 'Failed to update session') });
      });
  };

  if (care.retry) {
    return (
      <CareSaveRetry
        message={care.retry.message}
        busy={care.retry.busy}
        onRetry={care.retry_save}
        onClose={props.on_close}
      />
    );
  }
  if (loading) return null;
  const edit = edit_reason_of(form, session_details);
  const capture = is_cancel_capture(form.status, session_details, care.draft);
  const picker = {
    sessionStartEpoch: session_details.epoch_start_time,
    ianaTimezone: IST,
    timezoneLabel: 'IST',
    error: form.cancel_bucket_error
  };
  return (
    <>
      <h5>Edit Session Details</h5>
      <div style={{ margin: '10px 0' }}>
        <CustomSingleSelect
          label="Change status"
          options={session_status_values}
          onChange={on_field}
          value={form.status || ''}
          style={{ width: '300px' }}
          name="status"
        />
      </div>
      {form.status === 'CANCELLED'
        ? cancel_options.map((option, index) => (
            <td key={index}>
              <CustomRadioButton
                label={
                  option === 'cancelled_by_student'
                    ? 'Cancelled by Student'
                    : 'Cancelled by Teacher'
                }
                onRadioClick={() => setForm((f) => ({ ...f, ...toggle_cancel_patch(f, option) }))}
                selected={form[option]}
              />
            </td>
          ))
        : null}
      {capture && form.cancel_initiator_error ? (
        <p style={error_style}>
          * Choose who asked for the cancellation: Cancelled by Student or Cancelled by Teacher
        </p>
      ) : null}
      {form.status === 'CANCELLED' && form.cancelled_by_teacher ? (
        care_owns_cancel_time(form, session_details, care_record) ? (
          <p>{CARE_OWNS_CANCEL_TIME_NOTE}</p>
        ) : capture ? (
          // the single time field is the Care draft's "Requested at (IST)" below
          <TeacherCancelBucketPicker requestedAt={care.draft.requested_at} {...picker} />
        ) : (
          <TeacherCancelBucketPicker
            requestedAt={form.teacher_cancel_requested_at}
            onRequestedAtChange={(requested_at) =>
              set({ teacher_cancel_requested_at: requested_at, cancel_bucket_error: false })
            }
            {...picker}
          />
        )
      ) : null}
      {capture ? (
        <CareRequestFields
          draft={care.draft}
          errors={care.errors}
          onChange={patch_care}
          showInitiatedBy={false}
        />
      ) : null}
      <CustomInput
        label="Enter Penalty"
        style={{ width: '300px' }}
        value={form.teacher_penalty}
        name="teacher_penalty"
        onChange={on_field}
        type="number"
      />
      <div style={{ margin: '10px 0' }}>
        <textarea
          value={form.teacher_penalty_reason}
          name="teacher_penalty_reason"
          onChange={on_field}
          style={{ width: '300px', height: '100px' }}
          maxLength="200"
          placeholder="Enter penalty reason (max 200 char)"
        />
      </div>
      <EditReasonField
        edit={edit}
        value={form.status_change_reason}
        name="status_change_reason"
        onChange={on_field}
        error={form.status_reason_error}
      />
      <CustomCheckBox
        label="Ignore Rating"
        checked={ignore_rating}
        onClick={props.on_toggle_ignore}
      />
      {ignore_rating ? (
        <div style={{ margin: '10px 0' }}>
          <textarea
            value={ignore_rating_reason}
            name="ignore_rating_reason"
            onChange={on_ignore_reason}
            style={{ width: '300px', height: '100px' }}
            maxLength="200"
            placeholder="Enter ignore rating reason (max 255 char)"
          />
        </div>
      ) : null}
      {form.modal_error ? (
        <p style={error_style}>* Teacher penaly should be greater or equal to 0</p>
      ) : null}
      {form.rating_error ? <p style={error_style}>* Ignore reason cannot be empty</p> : null}
      {form.penalty_reason_error ? (
        <p style={error_style}>* Penalty reason cannot be empty</p>
      ) : null}
      {form.save_modal_error ? <p style={error_style}>* {form.save_modal_error}</p> : null}
      <Button onClick={save}>Save</Button>
    </>
  );
}

EditSessionForm.propTypes = {
  session_uuid: PropTypes.string,
  session_details: PropTypes.object.isRequired,
  loading: PropTypes.bool,
  ignore_rating: PropTypes.bool,
  ignore_rating_reason: PropTypes.string,
  on_toggle_ignore: PropTypes.func.isRequired,
  on_page_field_change: PropTypes.func.isRequired,
  set_loading: PropTypes.func.isRequired,
  on_saved: PropTypes.func.isRequired,
  on_save_failed: PropTypes.func.isRequired,
  on_close: PropTypes.func.isRequired,
  on_care_saved: PropTypes.func.isRequired,
  on_retry_change: PropTypes.func.isRequired,
  notify: PropTypes.func.isRequired
};

EditSessionForm.defaultProps = {
  session_uuid: '',
  loading: false,
  ignore_rating: false,
  ignore_rating_reason: ''
};

export default EditSessionForm;
